import * as React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tab,
    Typography,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import HistoryIcon from "@mui/icons-material/History";
import { formatDistanceToNow } from "date-fns";
import {
    selectRecentActivitySessions,
    selectRecentActivityResources,
    selectRecentActivityUsers,
    selectRecentActivityRegistrations,
} from "../../app/slices/recentActivitySlice";
import {
    RecentActivityItem,
    EntityType,
    buildDetailUrl,
} from "../../services/types/recentActivityTypes";

/**
 * Returns the appropriate icon for an entity type.
 */
function getEntityIcon(entityType: EntityType): React.ReactNode {
    switch (entityType) {
        case "session":
            return <EventIcon fontSize="small" />;
        case "resource":
            return <BadgeIcon fontSize="small" />;
        case "user":
            return <PersonIcon fontSize="small" />;
        case "registration":
            return <HowToRegIcon fontSize="small" />;
        default:
            return <HistoryIcon fontSize="small" />;
    }
}

interface RecentActivityListProps {
    items: RecentActivityItem[];
    onItemClick: (item: RecentActivityItem) => void;
    showEntityIcon?: boolean;
}

const RecentActivityList: React.FC<RecentActivityListProps> = ({
    items,
    onItemClick,
    showEntityIcon = false,
}) => {
    if (items.length === 0) {
        return (
            <Box sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                    No recent activity
                </Typography>
            </Box>
        );
    }

    return (
        <List dense disablePadding>
            {items.map((item) => (
                <ListItemButton
                    key={`${item.entityType}-${item.entityId}`}
                    onClick={() => onItemClick(item)}
                    sx={{ py: 1 }}
                >
                    {showEntityIcon && (
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            {getEntityIcon(item.entityType)}
                        </ListItemIcon>
                    )}
                    <ListItemText
                        primary={item.entityName}
                        secondary={
                            <Box component="span" sx={{ display: "flex", flexDirection: "column" }}>
                                {item.subtitle && item.subtitle !== item.entityName && (
                                    <Typography
                                        component="span"
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {item.subtitle}
                                    </Typography>
                                )}
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.disabled"
                                >
                                    {formatDistanceToNow(new Date(item.lastAccessedUtc), {
                                        addSuffix: true,
                                    })}
                                </Typography>
                            </Box>
                        }
                        primaryTypographyProps={{
                            variant: "body2",
                            noWrap: true,
                            title: item.entityName,
                        }}
                    />
                </ListItemButton>
            ))}
        </List>
    );
};

export interface RecentActivityPanelProps {
    /** Maximum height of the panel. Defaults to 400px. */
    maxHeight?: number | string;
}

const RecentActivityPanel: React.FC<RecentActivityPanelProps> = ({ maxHeight = 400 }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState("sessions");

    const sessions = useSelector(selectRecentActivitySessions);
    const resources = useSelector(selectRecentActivityResources);
    const users = useSelector(selectRecentActivityUsers);
    const registrations = useSelector(selectRecentActivityRegistrations);

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
    };

    const handleItemClick = (item: RecentActivityItem) => {
        // For users, pass the email (stored in entityName) to build the query string URL
        const email = item.entityType === "user" ? item.entityName : undefined;
        const url = buildDetailUrl(item.entityType, item.entityId, email);
        navigate(url);
    };

    const getTabLabel = (label: string, count: number): string => {
        return count > 0 ? `${label} (${count})` : label;
    };

    return (
        <Box sx={{ width: "100%", maxHeight, display: "flex", flexDirection: "column" }}>
            <TabContext value={activeTab}>
                <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                    <TabList
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="Recent activity tabs"
                    >
                        <Tab
                            label={getTabLabel("Sessions", sessions.length)}
                            value="sessions"
                            sx={{ textTransform: "none", minWidth: "auto" }}
                        />
                        <Tab
                            label={getTabLabel("Registrations", registrations.length)}
                            value="registrations"
                            sx={{ textTransform: "none", minWidth: "auto" }}
                        />
                        <Tab
                            label={getTabLabel("Resources", resources.length)}
                            value="resources"
                            sx={{ textTransform: "none", minWidth: "auto" }}
                        />
                        <Tab
                            label={getTabLabel("Users", users.length)}
                            value="users"
                            sx={{ textTransform: "none", minWidth: "auto" }}
                        />
                    </TabList>
                </Box>
                <Box sx={{ overflow: "auto", flex: 1 }}>
                    <TabPanel value="sessions" sx={{ p: 0 }}>
                        <RecentActivityList items={sessions} onItemClick={handleItemClick} />
                    </TabPanel>
                    <TabPanel value="registrations" sx={{ p: 0 }}>
                        <RecentActivityList items={registrations} onItemClick={handleItemClick} />
                    </TabPanel>
                    <TabPanel value="resources" sx={{ p: 0 }}>
                        <RecentActivityList items={resources} onItemClick={handleItemClick} />
                    </TabPanel>
                    <TabPanel value="users" sx={{ p: 0 }}>
                        <RecentActivityList items={users} onItemClick={handleItemClick} />
                    </TabPanel>
                </Box>
            </TabContext>
        </Box>
    );
};

export default RecentActivityPanel;
